import { animals } from "./animals";
import { masterpieces } from "./masterpieces";
import { allFlags } from "./worldcup";

export interface Reward {
  emoji: string;
  name: string;
}

// Milestone "animal friend" rewards: a new companion every 5 drawings, growing
// from a tiny mouse all the way up to an enormous dinosaur. Listed smallest →
// biggest so they visibly grow. Each name maps to a drawable friend in
// FRIEND_DRAWINGS (friends.ts).
export const REWARDS: Reward[] = [
  { emoji: "🐭", name: "Mouse" },
  { emoji: "🐸", name: "Frog" },
  { emoji: "🐹", name: "Hamster" },
  { emoji: "🦆", name: "Duck" },
  { emoji: "🐰", name: "Bunny" },
  { emoji: "🐱", name: "Cat" },
  { emoji: "🦊", name: "Fox" },
  { emoji: "🐶", name: "Dog" },
  { emoji: "🐧", name: "Penguin" },
  { emoji: "🐷", name: "Pig" },
  { emoji: "🐴", name: "Pony" },
  { emoji: "🐮", name: "Cow" },
  { emoji: "🦁", name: "Lion" },
  { emoji: "🐎", name: "Horse" },
  { emoji: "🐻", name: "Bear" },
  { emoji: "🐘", name: "Elephant" },
  { emoji: "🐳", name: "Whale" },
  { emoji: "🦕", name: "Dinosaur" },
];

// How many drawings between each new animal friend.
export const REWARD_EVERY = 5;

// Every drawing the child can complete (all levels + the famous paintings +
// the flags, which now count toward the trophies like any other drawing).
export const TOTAL_DRAWINGS = animals.length + masterpieces.length + allFlags.length;

// The ids of every drawing that counts toward the reward shelf — the exact same
// set the sticker page tallies. The Shape-Building section is a separate toddler
// track and never counts here, so finishing a shape must not nudge the reward
// milestones (that mismatch made the shelf lag a step behind the celebration).
const REWARD_ELIGIBLE_IDS = new Set<string>([
  ...animals.map((a) => a.id),
  ...masterpieces.map((m) => m.id),
  ...allFlags.map((f) => f.id),
]);

/**
 * How many finished drawings count toward the animal-friend rewards. This is the
 * SAME tally the sticker page shows, so a reward earned in the celebration and
 * the reward shown on the shelf can never disagree. Use this — never the raw
 * `completed.size`, which also includes Shape-Building drawings.
 */
export function rewardProgress(completed: Set<string>): number {
  let n = 0;
  for (const id of completed) if (REWARD_ELIGIBLE_IDS.has(id)) n++;
  return n;
}

export interface RewardTier extends Reward {
  /** Drawings needed to unlock. The biggest friend is capped at the whole
   *  collection so it's always earnable as the grand prize. */
  need: number;
  /** Relative display size, so friends visibly grow from small to big. */
  size: number;
}

/** The reward shelf with concrete unlock thresholds for the given collection. */
export function rewardTiers(totalAll: number = TOTAL_DRAWINGS): RewardTier[] {
  return REWARDS.map((r, i) => ({
    ...r,
    need: Math.min((i + 1) * REWARD_EVERY, totalAll),
    size: 1.6 + i * 0.18,
  }));
}

/**
 * The friend *just* unlocked by reaching `totalDone` drawings — i.e. this count
 * lands exactly on a milestone. Returns undefined between milestones, so the
 * celebration only shows a reward at the moment it's earned.
 */
export function rewardJustUnlocked(
  totalDone: number,
  totalAll: number = TOTAL_DRAWINGS,
): RewardTier | undefined {
  return rewardTiers(totalAll).find((r) => r.need === totalDone);
}
